import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogType?: string;
  ogImage?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = "website",
  ogImage,
  articlePublishedTime,
  articleModifiedTime,
}: SEOHeadProps) {
  const siteName = "韩漫之家";
  const fullTitle = `${title} - ${siteName}`;
  const baseUrl = "https://www.hanmanzhijia.com";
  const fullCanonical = `${baseUrl}${canonicalUrl}`;

  useEffect(() => {
    document.title = fullTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const removeMeta = (name: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      const el = document.querySelector(`meta[${attr}="${name}"]`);
      if (el) el.remove();
    };

    setMeta("description", description);
    setMeta("keywords", keywords);

    const canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonicalEl) {
      canonicalEl.setAttribute("href", fullCanonical);
    } else {
      const link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      link.setAttribute("href", fullCanonical);
      document.head.appendChild(link);
    }

    setMeta("og:title", fullTitle, true);
    setMeta("og:description", description, true);
    setMeta("og:type", ogType, true);
    setMeta("og:url", fullCanonical, true);
    setMeta("og:site_name", siteName, true);
    setMeta("og:locale", "zh_CN", true);
    if (ogImage) {
      setMeta("og:image", ogImage, true);
    } else {
      removeMeta("og:image", true);
    }

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", description);
    if (ogImage) {
      setMeta("twitter:image", ogImage);
    } else {
      removeMeta("twitter:image");
    }

    if (articlePublishedTime) {
      setMeta("article:published_time", articlePublishedTime, true);
    } else {
      removeMeta("article:published_time", true);
    }
    if (articleModifiedTime) {
      setMeta("article:modified_time", articleModifiedTime, true);
    } else {
      removeMeta("article:modified_time", true);
    }

    return () => {
      // Cleanup not strictly needed as next page will overwrite
    };
  }, [fullTitle, description, keywords, fullCanonical, ogType, ogImage, articlePublishedTime, articleModifiedTime]);

  return null;
}